import { Link } from '@tanstack/react-router'
import type { OverviewData } from '@workspace/api-backend'
import type { EvalCategory } from '@workspace/seedforge-ui/evaluation'
import {
  CategoryCard,
  EvaluationFeedback,
  type EvaluationFeedbackData,
  EvaluationRiskList,
  EvaluationSummary,
  EvaluationTensions,
  OnlyYouNote,
  VerdictHero,
} from '@workspace/seedforge-ui/evaluation'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import {
  ArrowRight,
  Circle,
  Lightbulb,
  Loader2,
  MessageCircleQuestion,
  Mic,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { useStartupContext } from '../../../contexts/startup-guard'
import { useCreateFeedbackMutation, useOverviewQuery } from '../../../hooks/api'
import { useBasePath } from '../../../hooks/use-base-path'
import { useMarkFeedbackSeen } from '../hooks/use-feedback-seen'
import { ImproveScoreCta } from './improve-score-cta'
import { RunFeedbackButton } from './run-feedback-button'

interface FeedbackDashboardProps {
  startupId: string
  onStartSession: () => void
}

const ICON_MAP: Record<string, ReactNode> = {
  users: <Users className="size-4" />,
  'trending-up': <TrendingUp className="size-4" />,
  lightbulb: <Lightbulb className="size-4" />,
  target: <Target className="size-4" />,
  circle: <Circle className="size-4" />,
}

function iconFromKey(key: string): ReactNode {
  return ICON_MAP[key] ?? <Circle className="size-4" />
}

function mapCategories(apiCategories: OverviewData['categories']): EvalCategory[] {
  return apiCategories.map((cat) => ({
    key: cat.key,
    label: cat.label,
    icon: iconFromKey(cat.iconKey),
    status: cat.status,
    score: cat.score,
    summary: cat.summary,
    findings: cat.findings.map((f) => ({
      text: f.text,
      type: f.type,
      detail: f.detail,
    })),
    nextStep: cat.nextStep,
  }))
}

export function FeedbackDashboard({ startupId, onStartSession }: FeedbackDashboardProps) {
  const basePath = useBasePath()
  const { billing } = useStartupContext()
  const { data, isLoading, error } = useOverviewQuery(startupId)
  const feedbackMutation = useCreateFeedbackMutation()

  // Reaching this page is what "seen" means, whichever way the founder got
  // here — the sidebar dot, the Home card, or the email link.
  useMarkFeedbackSeen(
    startupId,
    (data?.data as { evaluationId?: string | null } | undefined)?.evaluationId
  )

  const handleFeedbackSubmit = useCallback(
    (feedbackData: EvaluationFeedbackData) => {
      const evalId = (data?.data as { evaluationId?: string | null } | undefined)?.evaluationId
      feedbackMutation.mutate({
        body: {
          message: feedbackData.comment,
          vote: feedbackData.vote ?? undefined,
          rating: feedbackData.rating ?? undefined,
          evaluationId: evalId ?? undefined,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        },
      })
    },
    [feedbackMutation, data]
  )

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="size-10 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !data?.data) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center text-muted-foreground">
        Failed to load feedback
      </div>
    )
  }

  const overview = data.data
  const categories = mapCategories(overview.categories)

  return (
    <>
      <VerdictHero
        label="How SeedForge sees you"
        timestamp={overview.timestamp}
        summary={overview.summary}
        verdict={overview.verdict}
        verdictStatus={overview.verdictStatus}
        parameters={overview.parameters.map((p) => ({
          key: p.key,
          label: p.label,
          icon: iconFromKey(p.iconKey),
          status: p.status,
        }))}
        score={overview.score}
        scoreHistory={overview.scoreHistory}
        nextMoves={overview.nextMoves}
        onStartSession={onStartSession}
        evaluationTo={
          overview.evaluationId ? `${basePath}/evaluation/${overview.evaluationId}` : undefined
        }
        actions={overview.isFeedbackStale && <RunFeedbackButton startupId={startupId} />}
        privacyNote={<OnlyYouNote className="mb-0" />}
        banner={
          <>
            {billing?.promoSession && <PromoPitchBanner onStartSession={onStartSession} />}
            {overview.isReevaluating && <ReevaluationProgressBanner />}
            <EvaluationProcessingBanner status={overview.evaluationStatus} />
            <FeedbackWelcomeCard />
          </>
        }
      />

      <div className="mx-auto max-w-6xl pb-24">
        <div className="mt-8">
          <EvaluationResultsBanner
            status={overview.evaluationStatus}
            evaluationId={overview.evaluationId}
            basePath={basePath}
            timestamp={overview.timestamp}
          />
        </div>

        {overview.evaluationSummary && <EvaluationSummary summary={overview.evaluationSummary} />}

        {categories.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/80">
              Detailed Breakdown
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
              {categories.map((cat, i) => (
                <div
                  key={cat.key}
                  className={cn(
                    'flex',
                    categories.length === 5 && i < 2 && 'md:col-span-3',
                    categories.length === 5 && i >= 2 && 'md:col-span-2',
                    categories.length !== 5 && 'md:col-span-3'
                  )}
                >
                  <CategoryCard category={cat} className="flex-1" />
                </div>
              ))}
            </div>
          </section>
        )}

        <EvaluationRiskList risks={overview.risks} />

        {overview.tensions.length > 0 && <EvaluationTensions tensions={overview.tensions} />}

        {overview.openQuestions.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/80">
              What Investors Will Ask
            </h2>
            <div className="space-y-3">
              {overview.openQuestions.map((q) => (
                <div
                  key={q.title}
                  className="rounded-xl border border-border/60 bg-card/30 px-5 py-4"
                >
                  <div className="flex items-start gap-3">
                    <MessageCircleQuestion className="mt-0.5 size-4 shrink-0 text-amber-400" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{q.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground/70">
                        {q.detail}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <ImproveScoreCta startupId={startupId} onStartSession={onStartSession} />

        <EvaluationFeedback
          className="mt-10"
          title="Was this feedback useful?"
          description="Your feedback helps us improve our evaluation"
          onSubmit={handleFeedbackSubmit}
        />
      </div>
    </>
  )
}

// ─── Banners ─────────────────────────────────────────────────────────────────

function ReevaluationProgressBanner() {
  return (
    <div className="mb-6 flex items-center gap-3 rounded-xl border border-[#f97316]/20 bg-[#f97316]/3 px-5 py-4">
      <Loader2 className="size-4 shrink-0 animate-spin text-[#f97316]" />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">Running feedback on your pitch deck…</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          We're scoring your deck and refreshing your matched investors. This usually takes 2–5
          minutes, so feel free to keep working.
        </p>
      </div>
    </div>
  )
}

function EvaluationProcessingBanner({ status }: { status: 'none' | 'processing' | 'completed' }) {
  if (status !== 'processing') return null

  return (
    <div className="mb-6 flex items-center gap-4 rounded-xl border border-[#f97316]/20 bg-[#f97316]/3 px-5 py-4">
      <div className="relative flex size-8 items-center justify-center">
        <div className="absolute size-8 animate-ping rounded-full bg-[#f97316]/10" />
        <div className="relative size-2 rounded-full bg-[#f97316]" />
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">Your AI Session is being evaluated...</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Our investor council is reviewing your pitch. This usually takes 2–5 minutes.
        </p>
      </div>
    </div>
  )
}

function EvaluationResultsBanner({
  status,
  evaluationId,
  basePath,
  timestamp,
}: {
  status: 'none' | 'processing' | 'completed'
  evaluationId: string | null
  basePath: string
  timestamp?: string
}) {
  if (status !== 'completed' || !evaluationId) return null

  return (
    <Link
      to={`${basePath}/evaluation/${evaluationId}` as string}
      className="flex items-center gap-4 rounded-xl border border-emerald-400/20 bg-emerald-400/3 px-5 py-4 transition-colors hover:bg-emerald-400/6"
    >
      <div className="flex size-8 items-center justify-center rounded-full bg-emerald-400/10">
        <Sparkles className="size-4 text-emerald-400" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">Latest AI Session Results</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Your evaluation is ready. See how investors would score your startup.
          {timestamp && <span className="text-muted-foreground/50"> · {timestamp}</span>}
        </p>
      </div>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  )
}

function PromoPitchBanner({ onStartSession }: { onStartSession: () => void }) {
  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={onStartSession}
        className="flex w-full items-center gap-4 rounded-xl border border-emerald-400/20 bg-emerald-400/3 px-5 py-4 text-left transition-colors hover:bg-emerald-400/6"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-400/10">
          <Mic className="size-4 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              Try your first AI Pitch Session, free
            </h3>
            <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
              Limited time
            </span>
          </div>
          <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground/80">
            Pitch to our AI investor council for 30 minutes. Get scored on team, market, traction &
            vision. Free for a limited time, no credit card required.
          </p>
        </div>
        <ArrowRight className="size-4 shrink-0 text-emerald-400" />
      </button>
    </div>
  )
}

// ─── Welcome card ────────────────────────────────────────────────────────────

const WELCOME_DISMISSED_KEY = 'sf-feedback-welcome-dismissed'

function FeedbackWelcomeCard() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!localStorage.getItem(WELCOME_DISMISSED_KEY)) {
      setVisible(true)
    }
  }, [])

  if (!visible) return null

  const dismiss = () => {
    localStorage.setItem(WELCOME_DISMISSED_KEY, '1')
    setVisible(false)
  }

  return (
    <div className="mb-6">
      <div className="relative flex gap-4 rounded-xl border border-[#f97316]/20 bg-[#f97316]/6 px-5 py-4">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#f97316]/10">
          <Sparkles className="size-4 text-[#f97316]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">Welcome to Feedback</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground/80">
            See your startup the way investors will. Feedback pulls from your pitch deck, LinkedIn,
            website, and uploaded documents to highlight strengths, spot gaps, and show you what to
            fix first. It gets more accurate every time you add data or run an AI Session.
          </p>
          <SecondaryButton size="sm" className="mt-2.5" onClick={dismiss}>
            Got it
          </SecondaryButton>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-2.5 top-2.5 rounded-md p-1 text-muted-foreground/30 transition-colors hover:text-muted-foreground/60"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
